import { Component, inject, signal, computed, OnInit, OnDestroy, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageModule } from 'primeng/message';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CloudApiService, PlistBackupVersion } from '@/cloud/services/cloud-api.service';
import { DeviceContextService } from '../device-context.service';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';

interface PropertyListItem {
    key: string;
    value: any;
    type: string;
}

@Component({
    selector: 'app-cloud-property-list',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TableModule,
        TagModule,
        ButtonModule,
        TooltipModule,
        DialogModule,
        InputTextModule,
        TextareaModule,
        SelectModule,
        ConfirmDialogModule,
        MessageModule,
        SectionCardComponent,
        EmptyStateComponent
    ],
    templateUrl: './cloud-property-list.html',
    styleUrl: './cloud-property-list.scss',
    providers: [ConfirmationService]
})
export class CloudPropertyList implements OnInit, OnDestroy {
    private api = inject(CloudApiService);
    private ctx = inject(DeviceContextService);
    private messageService = inject(MessageService);
    private confirmationService = inject(ConfirmationService);
    private destroyRef = inject(DestroyRef);

    items = signal<PropertyListItem[]>([]);
    loading = signal(true);
    tunnelConnected = signal(false);
    errorMessage = signal<string | null>(null);

    // Backup state
    backupVersions = signal<PlistBackupVersion[]>([]);
    /** Set when the table is showing a stored backup because the device is offline */
    offlineBackupDate = signal<string | null>(null);
    readOnly = computed(() => !this.tunnelConnected());

    backupsDialogVisible = false;
    backupsLoading = signal(false);
    versionViewerVisible = false;
    viewedVersion = signal<{ meta: PlistBackupVersion; items: PropertyListItem[] } | null>(null);
    restoring = signal(false);
    capturing = signal(false);

    // Dialog state
    dialogVisible = false;
    dialogMode: 'add' | 'edit' = 'add';
    editingItem: { key: string; value: string; type: string } = { key: '', value: '', type: 'string' };

    typeOptions = [
        { label: 'String', value: 'string' },
        { label: 'Number', value: 'number' },
        { label: 'Boolean', value: 'boolean' },
        { label: 'JSON', value: 'object' }
    ];

    private deviceId: string | null = null;

    ngOnInit(): void {
        const device = this.ctx.device();
        if (device) {
            this.deviceId = device.id;
            // Subscribe to property list change notifications
            this.api.subscribe(`device:${this.deviceId}:propertyList:changed`);
            this.api.getDevicePropertyListChanged(this.deviceId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.loadData());
            // Refresh the versions list when a new backup is stored
            this.api.subscribe(`devices:${this.deviceId}:plistBackups:created`);
            this.api.getDevicePlistBackupsChanged(this.deviceId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.loadBackupVersions());
        }
        this.loadData();
    }

    ngOnDestroy(): void {
        if (this.deviceId) {
            this.api.unsubscribe(`device:${this.deviceId}:propertyList:changed`);
            this.api.unsubscribe(`devices:${this.deviceId}:plistBackups:created`);
        }
    }

    loadData(): void {
        const device = this.ctx.device();
        if (!device) {
            this.errorMessage.set('Device not loaded');
            this.loading.set(false);
            return;
        }

        this.loading.set(true);
        this.errorMessage.set(null);

        this.api.getPropertyList(device.id).then(response => {
            this.tunnelConnected.set(true);
            this.offlineBackupDate.set(null);
            this.items.set(response.items || []);
            this.loading.set(false);
        }).catch(error => {
            const msg = typeof error === 'string' ? error : (error?.error || error?.message || 'Failed to load property list');
            if (msg.includes('not connected via tunnel') || msg.includes('tunnel')) {
                this.tunnelConnected.set(false);
                this.loadOfflineFallback(device.id);
            } else {
                // A non-tunnel error (e.g. gateway timeout) doesn't prove the tunnel is up.
                // While showing offline-backup data, stay read-only so stale entries can
                // never be edited and written back over live device values.
                if (!this.offlineBackupDate()) {
                    this.tunnelConnected.set(true);
                }
                this.errorMessage.set(msg);
                this.loading.set(false);
            }
        });
    }

    /** Device is offline - show the latest stored backup read-only instead of nothing */
    private loadOfflineFallback(deviceId: string): void {
        this.api.getPlistBackups(deviceId).then(response => {
            const versions = response.versions || [];
            this.backupVersions.set(versions);
            if (versions.length === 0) {
                this.offlineBackupDate.set(null);
                this.loading.set(false);
                return;
            }
            return this.api.getPlistBackup(deviceId, versions[0].id).then(backup => {
                this.items.set(this.payloadToItems(backup.payload));
                this.offlineBackupDate.set(backup.createdUtc);
                this.loading.set(false);
            });
        }).catch(() => {
            this.offlineBackupDate.set(null);
            this.loading.set(false);
        });
    }

    private payloadToItems(payload: Record<string, unknown> | null | undefined): PropertyListItem[] {
        if (!payload) return [];
        return Object.entries(payload).map(([key, value]) => ({
            key,
            value,
            type: this.deriveType(value)
        }));
    }

    /** Mirrors the device-side type mapping (string|number|boolean|object|array|null) */
    private deriveType(value: unknown): string {
        if (value === null || value === undefined) return 'null';
        if (Array.isArray(value)) return 'array';
        switch (typeof value) {
            case 'string': return 'string';
            case 'number': return 'number';
            case 'boolean': return 'boolean';
            case 'object': return 'object';
            default: return 'unknown';
        }
    }

    // --- Backup versions dialog ---

    showBackupsDialog(): void {
        this.backupsDialogVisible = true;
        this.loadBackupVersions();
    }

    loadBackupVersions(): void {
        if (!this.deviceId) return;
        this.backupsLoading.set(true);
        this.api.getPlistBackups(this.deviceId).then(response => {
            this.backupVersions.set(response.versions || []);
            this.backupsLoading.set(false);
        }).catch(error => {
            const msg = typeof error === 'string' ? error : (error?.message || 'Failed to load backups');
            this.messageService.add({ severity: 'error', summary: 'Backups', detail: msg });
            this.backupsLoading.set(false);
        });
    }

    viewBackupVersion(version: PlistBackupVersion): void {
        if (!this.deviceId) return;
        this.api.getPlistBackup(this.deviceId, version.id).then(backup => {
            this.viewedVersion.set({ meta: version, items: this.payloadToItems(backup.payload) });
            this.versionViewerVisible = true;
        }).catch(error => {
            const msg = typeof error === 'string' ? error : (error?.message || 'Failed to load backup');
            this.messageService.add({ severity: 'error', summary: 'Backup', detail: msg });
        });
    }

    downloadBackupVersion(version: PlistBackupVersion): void {
        if (!this.deviceId) return;
        const deviceId = this.deviceId;
        this.api.getPlistBackup(deviceId, version.id).then(backup => {
            const json = JSON.stringify(backup.payload ?? {}, null, 2);
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const timestamp = new Date(version.createdUtc).toISOString().replace(/[:.]/g, '-');
            const link = document.createElement('a');
            link.href = url;
            link.download = `${deviceId}_plist_${timestamp}.json`;
            link.click();
            URL.revokeObjectURL(url);
        }).catch(error => {
            const msg = typeof error === 'string' ? error : (error?.message || 'Failed to download backup');
            this.messageService.add({ severity: 'error', summary: 'Download Failed', detail: msg });
        });
    }

    confirmRestore(version: PlistBackupVersion): void {
        const date = new Date(version.createdUtc).toLocaleString();
        const emptyWarning = version.itemCount === 0 ? ' This backup is EMPTY - restoring it will remove all properties.' : '';
        this.confirmationService.confirm({
            message: `This will replace the entire property list on the device with the backup from ${date}. Existing keys not in the backup will be deleted.${emptyWarning}`,
            header: 'Confirm Restore',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Restore',
            acceptButtonStyleClass: 'p-button-danger',
            rejectLabel: 'Cancel',
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => this.restoreVersion(version)
        });
    }

    private restoreVersion(version: PlistBackupVersion): void {
        if (!this.deviceId) return;
        this.restoring.set(true);
        this.api.restorePlistBackup(this.deviceId, version.id).then(() => {
            this.restoring.set(false);
            this.messageService.add({ severity: 'success', summary: 'Restored', detail: 'Property list restored to device' });
            this.backupsDialogVisible = false;
            this.versionViewerVisible = false;
            this.loadData();
        }).catch(error => {
            this.restoring.set(false);
            const msg = typeof error === 'string' ? error : (error?.message || 'Failed to restore backup');
            this.messageService.add({ severity: 'error', summary: 'Restore Failed', detail: msg });
        });
    }

    backupNow(): void {
        if (!this.deviceId) return;
        this.capturing.set(true);
        this.api.capturePlistBackup(this.deviceId).then(result => {
            this.capturing.set(false);
            if (result.duplicate) {
                this.messageService.add({ severity: 'info', summary: 'Backup', detail: 'No changes since last backup' });
            } else {
                this.messageService.add({ severity: 'success', summary: 'Backup', detail: 'Backup saved' });
            }
            this.loadBackupVersions();
        }).catch(error => {
            this.capturing.set(false);
            const msg = typeof error === 'string' ? error : (error?.message || 'Failed to create backup');
            this.messageService.add({ severity: 'error', summary: 'Backup Failed', detail: msg });
        });
    }

    showAddDialog(): void {
        this.dialogMode = 'add';
        this.editingItem = { key: '', value: '', type: 'string' };
        this.dialogVisible = true;
    }

    showEditDialog(item: PropertyListItem): void {
        this.dialogMode = 'edit';
        this.editingItem = {
            key: item.key,
            value: this.formatValueForEdit(item.value, item.type),
            type: item.type
        };
        this.dialogVisible = true;
    }

    formatValueForEdit(value: any, type: string): string {
        if (type === 'object' || type === 'array') {
            return JSON.stringify(value, null, 2);
        }
        return String(value ?? '');
    }

    formatValueForDisplay(value: any, type: string): string {
        if (value === null || value === undefined) {
            return '<null>';
        }
        if (type === 'object' || type === 'array') {
            return JSON.stringify(value);
        }
        return String(value);
    }

    parseValue(valueStr: string, type: string): any {
        switch (type) {
            case 'number': {
                const num = Number(valueStr);
                if (isNaN(num)) throw new Error('Invalid number format');
                return num;
            }
            case 'boolean': {
                const lower = valueStr.toLowerCase();
                if (lower === 'true' || lower === '1') return true;
                if (lower === 'false' || lower === '0') return false;
                throw new Error('Invalid boolean format. Use true/false');
            }
            case 'object':
            case 'array':
                try {
                    return JSON.parse(valueStr);
                } catch {
                    throw new Error('Invalid JSON format');
                }
            default:
                return valueStr;
        }
    }

    saveItem(): void {
        const device = this.ctx.device();
        if (!device) return;

        if (!this.editingItem.key.trim()) {
            this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Key is required' });
            return;
        }

        let parsedValue: any;
        try {
            parsedValue = this.parseValue(this.editingItem.value, this.editingItem.type);
        } catch (error: any) {
            this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: error.message });
            return;
        }

        this.api.setPropertyListItem(device.id, this.editingItem.key, parsedValue).then(() => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: `Property "${this.editingItem.key}" saved` });
            this.dialogVisible = false;
            this.loadData();
        }).catch(error => {
            const msg = typeof error === 'string' ? error : (error?.message || 'Failed to save property');
            this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: msg });
        });
    }

    confirmDelete(item: PropertyListItem): void {
        this.confirmationService.confirm({
            message: `Are you sure you want to delete the property "${item.key}"? This action cannot be undone.`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Delete',
            acceptButtonStyleClass: 'p-button-danger',
            rejectLabel: 'Cancel',
            rejectButtonStyleClass: 'p-button-secondary',
            accept: () => this.deleteItem(item)
        });
    }

    deleteItem(item: PropertyListItem): void {
        const device = this.ctx.device();
        if (!device) return;

        this.api.deletePropertyListItem(device.id, item.key).then(() => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: `Property "${item.key}" deleted` });
            this.loadData();
        }).catch(error => {
            const msg = typeof error === 'string' ? error : (error?.message || 'Failed to delete property');
            this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: msg });
        });
    }

    getTypeSeverity(type: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
        switch (type) {
            case 'string': return 'info';
            case 'number': return 'success';
            case 'boolean': return 'warn';
            case 'object': return 'contrast';
            case 'array': return 'contrast';
            default: return 'secondary';
        }
    }
}
